import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Download, Building2, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBrochure = (url) => {
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Available Properties
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Browse our collection of exclusive property brochures from top builders
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
            <p className="mt-4 text-slate-600">Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <Card className="max-w-2xl mx-auto">
            <CardContent className="py-12 text-center">
              <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Properties Available</h3>
              <p className="text-slate-600">
                Check back soon for new property listings and brochures.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-[#1e3a8a]">Property Brochures</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-[#1e3a8a]" />
                          City
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-[#1e3a8a]" />
                          Builder
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-slate-700">
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="h-4 w-4 text-[#1e3a8a]" />
                          Brochure
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {properties.map((property) => (
                      <tr
                        key={property.id}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">{property.city}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-slate-700">{property.builder}</div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Button
                            onClick={() => handleViewBrochure(property.brochure_url)}
                            className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                            size="sm"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            View Brochure
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View */}
              <div className="md:hidden space-y-4 mt-6">
                {properties.map((property) => (
                  <Card key={property.id} className="border border-slate-200">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                            <MapPin className="h-4 w-4" />
                            City
                          </div>
                          <div className="font-medium text-slate-900">{property.city}</div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                            <Building2 className="h-4 w-4" />
                            Builder
                          </div>
                          <div className="text-slate-700">{property.builder}</div>
                        </div>
                        <Button
                          onClick={() => handleViewBrochure(property.brochure_url)}
                          className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          View Brochure
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
